import { DocumentId, DocumentIdSchema } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';

import {
  selectDocumentTypeArray,
  selectGenderTypeArray,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/config';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { AutocompleteNationalityFormField } from '@/components/shared/components/NewContextForms/AutocompleteNationalityFormField/AutocompleteNationalityFormField';
import { AutocompleteAddress } from '@/components/shared/components/NewContextForms/components/AutocompleteAddress/AutocompleteAddress';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { getBlankDefaultValuesFromZodSchema } from '@/utils/getBlankDefaultValuesFromZodSchema';

export interface DocumentIdFormProps extends FormGridOptions {
  documentId?: DocumentId;
  showBirthDateField?: boolean;
  showAddressFields?: boolean;
  showDetailedAddressForm?: boolean;
}

const blankDocumentIdFormValues = getBlankDefaultValuesFromZodSchema(DocumentIdSchema);

export const DocumentIdForm = ({
  documentId = blankDocumentIdFormValues,
  showBirthDateField = false,
  showDetailedAddressForm = false,
  showAddressFields = false,
  fullWidth,
  fieldVariant,
  direction,
}: DocumentIdFormProps) => {
  const fieldValues = useFormContext()?.getValues();
  const setValue = useFormContext()?.setValue;

  const mainResidenceAddress = fieldValues?.mainResidenceAddress;
  const documentAddress = fieldValues?.documentAddress;

  const copyDocumentAddressToMainResidence = () => {
    if (!documentAddress || !mainResidenceAddress) return;

    if (mainResidenceAddress.address === '') {
      setValue('mainResidenceAddress', documentAddress);
    }
    if (mainResidenceAddress.doorNumber === '') {
      setValue('mainResidenceAddress.doorNumber', documentAddress.doorNumber);
    }
    if (mainResidenceAddress.building === '') {
      setValue('mainResidenceAddress.building', documentAddress.building);
    }
    if (mainResidenceAddress.floorNumber === '') {
      setValue('mainResidenceAddress.floorNumber', documentAddress.floorNumber);
    }
  };

  return (
    <FormGridContainer fieldVariant={fieldVariant} fullWidth={fullWidth} direction={direction}>
      <TextFormField
        name={'firstName'}
        label={'FIRST_NAME'}
        value={documentId?.fullName?.firstName ?? ''}
        fieldVariant={fieldVariant}
      />
      <TextFormField
        name={'lastName'}
        label={'LAST_NAME'}
        value={documentId?.fullName?.lastName ?? ''}
        fieldVariant={fieldVariant}
      />
      <SelectFormField
        name={'documentType'}
        label={'DOCUMENT_TYPE'}
        value={documentId?.documentType ?? ''}
        fieldVariant={fieldVariant}
        selectOptions={selectDocumentTypeArray}
      />
      <TextFormField
        name={'documentNumber'}
        label={'DOCUMENT_ID_NUMBER'}
        value={documentId?.documentNumber ?? ''}
        fieldVariant={fieldVariant}
      />
      <DateFormField
        name={'expiration'}
        label={'EXPIRATION_DATE'}
        value={documentId?.expiration ?? ''}
        fieldVariant={fieldVariant}
        disablePast
      />
      <AutocompleteNationalityFormField
        name={'documentExpeditor'}
        label={'DOCUMENT_EXPEDITOR'}
        value={documentId?.documentExpeditor ?? ''}
        fieldVariant={fieldVariant}
      />
      <AutocompleteNationalityFormField
        name={'nationality'}
        label={'NATIONALITY'}
        value={documentId?.nationality ?? ''}
        fieldVariant={fieldVariant}
      />
      {showAddressFields && !showDetailedAddressForm && (
        <TextFormField
          name={'documentAddress.address'}
          label={'DOCUMENT_ADDRESS'}
          value={documentId?.documentAddress?.formattedAddress ?? ''}
          fieldVariant={fieldVariant}
        />
      )}
      {showAddressFields && (
        <TextFormField
          name={'documentAddress.floorNumber'}
          label={'FLOOR_NUMBER'}
          value={documentId?.documentAddress?.floorNumber ?? ''}
          fieldVariant={fieldVariant}
        />
      )}
      {showAddressFields && (
        <TextFormField
          name={'documentAddress.doorNumber'}
          label={'DOOR_NUMBER'}
          value={documentId?.documentAddress?.doorNumber ?? ''}
          fieldVariant={fieldVariant}
        />
      )}
      {showAddressFields && (
        <TextFormField
          name={'documentAddress.building'}
          label={'BUILDING_LABEL'}
          value={documentId?.documentAddress?.building ?? ''}
          fieldVariant={fieldVariant}
        />
      )}
      <TextFormField
        name={'birthPlace'}
        label={'BIRTH_PLACE'}
        value={documentId?.birthPlace ?? ''}
        fieldVariant={fieldVariant}
      />
      {showBirthDateField && (
        <DateFormField
          name={'birthDate'}
          label={'BIRTH_DATE'}
          value={documentId?.birthDate ?? ''}
          fieldVariant={fieldVariant}
          disableFuture
        />
      )}
      <SelectFormField
        name={'gender'}
        label={'GENDER'}
        value={documentId?.gender ?? ''}
        fieldVariant={fieldVariant}
        selectOptions={selectGenderTypeArray}
      />
      {!showAddressFields && useFormContext() && (
        <AutocompleteAddress
          fieldVariant={fieldVariant}
          fieldNamesPrefix="documentAddress"
          onSaveForm={copyDocumentAddressToMainResidence}
        />
      )}
    </FormGridContainer>
  );
};
